import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { thesisPackageStatus, thesisPackageUpdate } from "@/lib/api";
import HoverTip from "@/components/HoverTip";

const RED = "#B32A22";

const daysLabel = (n) => (n == null ? "" : n === 0 ? "hoy" : `hace ${n} d`);
const minutesAgo = (iso) => {
    const t = new Date(iso).getTime();
    return isNaN(t) ? null : Math.max(0, Math.round((Date.now() - t) / 60000));
};

// Message for a generation the backend refused (status from the /thesis/generate gate).
export function gateMessage(gate) {
    if (!gate) return null;
    if (gate.status === "package_busy") {
        const m = gate.busy?.started_at ? minutesAgo(gate.busy.started_at) : null;
        return `Otro usuario está generando ahora esta empresa${m != null ? ` (empezó hace ${m} min)` : ""}. Cuando termine, su generación quedará disponible para todos: el botón «Actualizar» se pondrá en rojo y podrás traerla a tu cuenta en unos segundos, sin esperar a una generación propia.`;
    }
    if (gate.status === "package_pending") {
        return `Hay una generación más reciente de esta empresa (${daysLabel(gate.age_days)}) que aún no has aplicado. Actualiza primero (unos segundos, sin coste); después, si quieres, podrás regenerarla cuando esté permitido.`;
    }
    if (gate.status === "package_fresh") {
        return `Esta empresa se generó ${daysLabel(gate.age_days)}: no ha dado tiempo material a que cambie nada, así que nadie puede regenerarla hasta dentro de ${gate.wait_days} día${gate.wait_days === 1 ? "" : "s"} (antes, solo si publica resultados).`;
    }
    return null;
}

/**
 * Shared generation of a company: one per company, for every user who follows it.
 * «Generar» (expensive, slow) refreshes it for everyone; «Actualizar» brings the latest
 * one into this user's account in seconds. The big button is red while the user's thesis
 * is behind the latest generation and black (disabled) when it is up to date.
 */
export default function SharedGeneration({ ticker, onUpdated, compact = false, gate = null, reloadSignal = 0 }) {
    const [st, setSt] = useState(null);
    const [updating, setUpdating] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async () => {
        if (!ticker) { setSt(null); return; }
        try { setSt(await thesisPackageStatus(ticker)); } catch { setSt(null); }
    }, [ticker]);

    useEffect(() => {
        const t = setTimeout(load, compact ? 400 : 0);   // typed tickers: wait for the user to stop
        return () => clearTimeout(t);
    }, [load, compact, reloadSignal]);

    // While another user is generating, check again every 20 s so the button turns red
    // as soon as their generation lands.
    useEffect(() => {
        clearInterval(timer.current);
        if (st?.busy && !st.busy.mine) timer.current = setInterval(load, 20000);
        return () => clearInterval(timer.current);
    }, [st, load]);

    if (!st || !st.exists) {
        const msg = gateMessage(gate);
        return msg ? <div className="border border-[#B8860B] bg-[#FBF3E0] p-3 text-sm text-[#7a5a10] mb-4" data-testid="shared-gen-gate">{msg}</div> : null;
    }

    const pending = !!st.pending;
    const busyOther = st.busy && !st.busy.mine;
    const update = async () => {
        setUpdating(true);
        try {
            const res = await thesisPackageUpdate(ticker);
            toast.success("Tesis actualizada con la última generación");
            await load();
            onUpdated && onUpdated(res);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo actualizar la tesis.");
        } finally {
            setUpdating(false);
        }
    };

    const genText = `Última generación de ${st.ticker}: ${daysLabel(st.age_days)}`;
    const regenText = st.can_generate
        ? (st.after_results ? "Ha publicado resultados desde entonces: ya se puede regenerar." : "Ya se puede regenerar.")
        : `Nadie puede regenerarla hasta dentro de ${st.wait_days} día${st.wait_days === 1 ? "" : "s"} (antes, solo si publica resultados).`;
    const tip = "La generación (búsquedas en la web e investigación con IA: lo caro y lento) se comparte entre todos los usuarios que siguen la empresa. "
        + "«Actualizar» trae la última a tu cuenta en unos segundos y sin coste: su plan y sus tesis de tendencia sustituyen a las tuyas; conservas tus KPI, documentos, conversaciones y carpetas. "
        + `Tras una generación nadie puede regenerar la empresa durante ${st.regen_min_days} días (no ha dado tiempo a que cambie nada), salvo que publique resultados.`;

    return (
        <div className={`border ${pending ? "border-[#B32A22]/50" : "border-black/20"} bg-white ${compact ? "p-3" : "p-4"} mb-4`} data-testid="shared-gen">
            {gateMessage(gate) && (
                <div className="border border-[#B8860B] bg-[#FBF3E0] p-3 text-sm text-[#7a5a10] mb-3" data-testid="shared-gen-gate">{gateMessage(gate)}</div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                    onClick={update}
                    disabled={!pending || updating}
                    className={`w-full sm:w-auto shrink-0 flex items-center justify-center gap-2 px-5 ${compact ? "py-2" : "py-3"} text-xs sm:text-sm uppercase tracking-[0.12em] font-semibold text-white transition-colors disabled:cursor-not-allowed`}
                    style={{ background: pending ? RED : "#111111", opacity: pending || updating ? 1 : 0.85 }}
                    data-testid="shared-gen-update-btn"
                >
                    {updating ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                    {updating ? "Actualizando…" : pending ? "Actualizar" : "Actualizada"}
                </button>
                <div className="text-xs text-[#4A4A4A] leading-relaxed min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-semibold text-black">{genText}</span>
                        <HoverTip text={tip} maxWidth={340}><span className="cursor-help text-[#4A4A4A]">ⓘ</span></HoverTip>
                    </div>
                    {pending ? (
                        <div style={{ color: RED }} data-testid="shared-gen-pending">
                            {st.has_thesis
                                ? `Tu tesis no la incluye todavía${st.pending_days != null ? ` (${st.pending_days} d sin actualizar)` : ""}: pulsa «Actualizar» para traerla en unos segundos.`
                                : "Aún no tienes esta empresa: pulsa «Actualizar» para traer su tesis completa en unos segundos, sin generarla."}
                        </div>
                    ) : (
                        <div>Tu tesis ya usa la última generación.</div>
                    )}
                    {busyOther ? (
                        <div className="text-[#7a5a10]" data-testid="shared-gen-busy">{gateMessage({ status: "package_busy", busy: st.busy })}</div>
                    ) : (
                        <div>{regenText}</div>
                    )}
                </div>
            </div>
        </div>
    );
}
